/**
 * Previews (For Twitch & YouTube & Kick) — stream thumbnail previews
 *
 * Declares one web-accessible-resource entry exposing two globs — `images/` and
 * `main/` — to `<all_urls>` with no `use_dynamic_url`, so every bundled image and
 * script is fetchable from any page at a fixed URL.
 *
 * The vendor-named `images/TP128.png` icon is the cleanest signal: it loads through
 * `probeImage` with no CORS grant, so it survives a restrictive page CSP.
 *
 * Manifest verified against version 17.3.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'hpmbiinljekjjcjgijnlbmgcmoonclah';

export const previews: ExtensionDefinition = {
  id: ID,
  name: 'Previews (For Twitch & YouTube & Kick)',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 60_000,
  notes:
    'Exposes images/ and main/ to every origin with no dynamic URL. The vendor-named ' +
    'TP128.png icon is conclusive and loads without a CORS grant.',
  signals: [
    {
      id: 'war-tp-icon-128',
      kind: 'web-accessible-resource',
      description:
        'Load images/TP128.png via <img> — a vendor-named icon exposed to every ' +
        'origin; needs no CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'images/TP128.png'),
    },
    {
      id: 'war-tp-icon-32',
      kind: 'web-accessible-resource',
      description:
        'Load images/TP32.png via <img> — a second vendor-named icon, corroborating ' +
        'the identification.',
      weight: 0.5,
      run: () => probeImage(ID, 'images/TP32.png'),
    },
    {
      id: 'war-core-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch main/core.js — a stable, vendor-named script under the exposed main/ ' +
        'glob, independent of the icon paths.',
      weight: 0.5,
      run: () => probeFetch(ID, 'main/core.js'),
    },
  ],
};
